import { Box } from '@/components/box/box';
import { GridContainer } from '@/components/grid-container/grid-container';
import { slugify } from '@/utils/slugify';
import { useMemo } from 'react';
import { DesktopNestedNavigation } from './components/desktop-nested-navigation';
import { WorkSubsection } from './components/work-subsection';
import { type WorkSection } from '@/lib/schemas/work-section';
import { WorkSubsection as WorkSubsectionType } from '@/lib/schemas/work-subsection';

type WorkSectionProps = {
  activeSubsectionTitle?: string;
  section: WorkSection;
  canAnimate: boolean;
};

export function WorkSection({
  activeSubsectionTitle,
  section,
  canAnimate,
}: WorkSectionProps) {
  const subsections = useMemo(() => {
    return processSubsectionsForDuplicateIds(
      section.subsections
        .filter(
          (subsection) => slugify(subsection.title) !== activeSubsectionTitle
        )
        .filter((subsection) => subsection.projects.length > 0)
    );
  }, [activeSubsectionTitle, section.subsections]);

  return (
    <Box>
      <GridContainer>
        <DesktopNestedNavigation subsections={subsections} />
      </GridContainer>

      <Box
        css={{
          '@bp3': {
            mt: '$6',
          },
          '@bp4': {
            mt: '$8',
          },
        }}
      >
        {subsections.map((subsection, index) => {
          return (
            <WorkSubsection
              key={`${subsection.title}-${index}`}
              subsection={subsection}
              canAnimate={canAnimate}
            />
          );
        })}
      </Box>
    </Box>
  );
}

/**
 * Subsections cannot contain duplicate projects, but the same project can appear
 * across multiple subsections on the same or different pages. For example, Page A
 * might contain "projectABC" in two subsections, while Page B contains it in three.
 *
 * We can't rely on subsection id/title for unique project IDs, as Framer Motion's
 * layoutId requires global uniqueness across pages for transitions to work. Instead,
 * we append a generic suffix to duplicate project IDs to ensure uniqueness.
 *
 * This function processes subsections, detects duplicate project IDs, and appends
 * a suffix to maintain uniqueness for use with Framer Motion's layoutId.
 */
function processSubsectionsForDuplicateIds(
  subsections: WorkSubsectionType[]
): WorkSubsectionType[] {
  // Map to track occurrences of each project ID
  const projectIds = new Map<string, number>();

  subsections.forEach((subsection) => {
    subsection.projects.forEach((project) => {
      const id = project.target._id;

      // Get the current count of the ID (if any)
      const count = projectIds.get(id);

      // If it's the first time encountering this ID, add it to the map
      if (!count) {
        projectIds.set(id, 1);
        return;
      }

      // If it's a duplicate, append a suffix to make the ID unique
      project.target._id = `${id}_${count + 1}`;

      // Update the count in the map for the next occurrence
      projectIds.set(id, count + 1);
    });
  });

  return subsections;
}
